import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Lock, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { slug } from "@/lib/format";
import { ConfirmDialog, Loading, PageHeader, btnGhost, inputCls } from "@/components/app/common";

const GROUPS = [
  { tipo: "costo", title: "Categorie di costo", text: "Per classificare spese e acquisti." },
  { tipo: "incasso", title: "Categorie di incasso", text: "Per classificare le entrate." },
  { tipo: "linea", title: "Tipologie prodotto", text: "Dimensione secondaria di analisi (es. Sieri, Creme viso). La dimensione principale è la marca." },
  { tipo: "uscita", title: "Tipi di uscita merce", text: "Vendita manuale, scarti e tipi personalizzati." },
  { tipo: "packaging", title: "Tipologie packaging", text: "Sottotipi degli articoli di packaging (es. Scatole, Adesivi). Usati per raggruppare il packaging in Magazzino." },
  { tipo: "spesa_brand", title: "Categorie spese brand", text: "Create al volo inserendo una spesa brand." },
  { tipo: "uscita", gruppo: "omaggio", key: "omaggio", title: "Causali omaggio", text: "Ogni causale è valorizzata al costo medio e scarica il magazzino. 'Omaggio generico' = altro." },
];

function Chip({ g, c, onDelete, onRename }) {
  const [edit, setEdit] = useState(null);
  const id = `${g.key || g.tipo}-${slug(c.nome)}`;
  const save = async (e) => { e.preventDefault(); if (edit.trim() && edit.trim() !== c.nome && await onRename(c, edit.trim())) setEdit(null); else if (edit.trim() === c.nome) setEdit(null); };
  if (edit !== null) return (
    <form onSubmit={save} className="inline-flex items-center gap-1">
      <Input autoFocus data-testid={`rename-categoria-input-${id}`} value={edit} onChange={(e) => setEdit(e.target.value)} onBlur={() => setEdit(null)} className="h-8 rounded-full text-sm w-52" />
    </form>
  );
  return (
    <span data-testid={`categoria-chip-${id}`} className="inline-flex items-center gap-1.5 text-sm pl-3 pr-1.5 py-1 rounded-full bg-[#F4EFEA] border border-[#EAE3DC]">
      {g.gruppo && <Gift className="h-3 w-3 text-[#8C6D62]" />}
      {onRename ? <button type="button" data-testid={`rename-categoria-${id}`} onClick={() => setEdit(c.nome)} className="hover:underline" title="Rinomina (con il nome di un'altra categoria le unisce)">{c.nome}</button> : c.nome}
      {c.protetta && <span data-testid={`protetta-categoria-${id}`} title="Protetta: non viene eliminata da «Elimina categorie non usate» (puoi rinominarla o eliminarla singolarmente)" className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-[#4E6656]"><ShieldCheck className="h-3 w-3" />protetta</span>}
      {c.sistema ? <span data-testid={`sistema-categoria-${id}`} title="Categoria di sistema: usata dalla logica dell'app, non eliminabile" className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-[#756B64] pr-1.5"><Lock className="h-3 w-3" />di sistema</span>
        : <button data-testid={`delete-categoria-${id}`} onClick={() => onDelete(c)} className="h-5 w-5 grid place-items-center rounded-full hover:bg-[#E07A5F]/15 text-[#756B64] hover:text-[#B4553D]" title="Elimina"><X className="h-3 w-3" /></button>}
    </span>
  );
}

function Group({ g, items, onAdd, onDelete, onRename }) {
  const [nome, setNome] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;
    if (await onAdd(g, nome.trim())) setNome("");
  };
  const key = g.key || g.tipo;
  const renamable = (c) => !c.sistema;
  return (
    <section className="card-soft p-6 fade-up" data-testid={`categorie-group-${key}`}>
      <h2 className="text-lg font-medium">{g.title}</h2>
      <p className="text-xs text-[#756B64] mt-1 mb-5">{g.text}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {items.map((c) => <Chip key={c.id} g={g} c={c} onDelete={onDelete} onRename={renamable(c) ? onRename : null} />)}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input data-testid={`new-categoria-input-${key}`} value={nome} onChange={(e) => setNome(e.target.value)} placeholder={g.gruppo ? "Nuova causale omaggio…" : "Nuova voce…"} className={inputCls} />
        <Button data-testid={`add-categoria-button-${key}`} type="submit" className="rounded-full h-10 w-10 p-0 bg-[#8C6D62] hover:bg-[#77594E] shrink-0"><Plus className="h-4 w-4" /></Button>
      </form>
    </section>
  );
}

export default function Categorie() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/categorie").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const add = async (g, nome) => {
    try { await api.post("/categorie", { tipo: g.tipo, nome, gruppo: g.gruppo || null }); toast.success(`"${nome}" aggiunta`); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const rename = async (c, nome) => {
    try { const { data } = await api.put(`/categorie/${c.id}`, { nome }); toast.success(data.unita ? `"${c.nome}" unita a "${data.nome}" (${data.spese_aggiornate} spese)` : `Rinominata in "${nome}"`); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const [bulk, setBulk] = useState(null);
  const openBulk = () => api.get("/categorie/non-usate").then((r) => (r.data.length ? setBulk(r.data) : toast.info("Nessuna categoria inutilizzata da eliminare"))).catch((e) => toast.error(errMsg(e)));
  const runBulk = async () => {
    try { const { data } = await api.post("/categorie/elimina-non-usate"); toast.success(`${data.eliminate} categorie eliminate`); setBulk(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/categorie/${c.id}`); toast.success(`"${c.nome}" eliminata`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="categorie-page">
      <PageHeader overline="Impostazioni" title="Categorie e tipologie" subtitle="Le categorie sono libere: si creano al volo dai moduli (anche dai collaboratori) con «+ Crea». Qui puoi rinominarle, unirle (rinomina con il nome di un'altra) ed eliminare quelle non usate. Le voci «di sistema» servono alla logica dell'app.">
        <Button variant="outline" className={btnGhost} onClick={openBulk} data-testid="categorie-elimina-non-usate"><Trash2 className="h-4 w-4 mr-1" />Elimina categorie non usate</Button>
      </PageHeader>
      {!items ? <Loading /> : (
        <div className="grid lg:grid-cols-2 gap-5">
          {GROUPS.map((g) => <Group key={g.key || g.tipo} g={g} items={items.filter((c) => c.tipo === g.tipo && (c.gruppo || null) === (g.gruppo || null))} onAdd={add} onDelete={remove} onRename={rename} />)}
        </div>
      )}
      <ConfirmDialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)} title={`Eliminare ${bulk?.length || 0} categorie non usate?`}
        text={bulk ? `Verranno eliminate: ${bulk.map((c) => c.nome).join(", ")}. Le categorie di sistema, protette e usate restano.` : ""} onConfirm={runBulk} confirmLabel="Elimina tutte" />
    </div>
  );
}
